import React, { createContext, useContext, useCallback, useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * ChartOptionsContext
 *
 * A lightweight registry that lets chart widgets register their ECharts
 * `option` objects so the export flow can include them in the request.
 *
 * Usage:
 *  1. Wrap the dashboard grid with <ChartOptionsProvider>
 *  2. In each chart widget:
 *       const option = useMemo(() => { ... }, [deps]);
 *       useRegisterChartOption(widget.id, option);
 *  3. In the export handler, call getChartOptions() to get all registered options
 */

const ChartOptionsContext = createContext(null);

export function ChartOptionsProvider({ children }) {
  const optionsRef = useRef({});

  const register = useCallback((widgetId, option) => {
    optionsRef.current[String(widgetId)] = option;
  }, []);

  const unregister = useCallback((widgetId) => {
    delete optionsRef.current[String(widgetId)];
  }, []);

  const getAll = useCallback(() => {
    const result = {};
    Object.entries(optionsRef.current).forEach(([id, option]) => {
      if (option) {
        result[id] = option;
      }
    });
    return Object.keys(result).length > 0 ? result : null;
  }, []);

  const value = useMemo(() => ({ register, unregister, getAll }), [register, unregister, getAll]);

  return <ChartOptionsContext.Provider value={value}>{children}</ChartOptionsContext.Provider>;
}

ChartOptionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook for chart widgets to register/unregister their ECharts option.
 * Call at the TOP of the component (before any early returns).
 *
 * @param {string|number} widgetId - The widget ID to register under
 * @param {Object|null} option - The ECharts option object, or null when no data
 *
 * The effect registers the option when it is non-null and unregisters on
 * unmount or when the option becomes null — so stale entries are cleaned up
 * automatically.
 */
export function useRegisterChartOption(widgetId, option) {
  const ctx = useContext(ChartOptionsContext);

  useEffect(() => {
    if (ctx && widgetId != null && option) {
      ctx.register(String(widgetId), option);
    }
    return () => {
      if (ctx && widgetId != null) {
        ctx.unregister(String(widgetId));
      }
    };
  }, [ctx, widgetId, option]);
}

/**
 * Hook for the export flow to retrieve all registered chart options.
 * Returns a function that, when called, returns { [widgetId]: echartsOption } or null.
 */
export function useChartOptions() {
  const ctx = useContext(ChartOptionsContext);

  if (process.env.NODE_ENV === 'development' && ctx == null) {
    // eslint-disable-next-line no-console
    console.warn(
      'useChartOptions: no ChartOptionsProvider found — chart options will not be available for export.',
    );
  }

  return ctx?.getAll || (() => null);
}
